import {
  Addons,
  EntityTypeEnum,
  Invitation,
  MembershipStatusEnum,
  RoleEnum,
  ServiceTypeEnum,
  User,
  UserRole,
  UserTypeEnum,
  Workspace,
  WorkspaceTypeEnum,
} from '@sr-sdks/api-sdk-axios';
import { useEffect } from 'react';
import { ALL_WORKSPACES } from '../../constant/Common';
import { reduxServices } from '../../reducers/reduxServices';
import { useTypedSelector } from '../../stateStore';
import { DeepPartial } from '../../types/commonTypes';
import { WorkspaceTask } from '../../types/taskTypes';
import { ApiLoadingStateEnum } from '../api/apiLoadingStateEnum';

export const getBusinessNumberLabel = (businessNumberCountry?: string) => {
  if (businessNumberCountry && businessNumberLabels[businessNumberCountry])
    return businessNumberLabels[businessNumberCountry];

  return 'Business Number';
};

export const businessNumberLabels: Record<string, string> = {
  AU: 'ABN',
  CH: 'UID',
  FR: 'SIREN/SIRET',
  GB: 'CRN',
  NZ: 'NZBN',
  US: 'EIN',
};

export const workspaceTypeIsAgent = (workspace?: Workspace) => {
  switch (workspace?.workspaceType) {
    case WorkspaceTypeEnum.BusinessAgency:
      return true;

    default:
      return false;
  }
};

export const workspaceTypeIsBusiness = (workspace?: Workspace) => {
  switch (workspace?.workspaceType) {
    case WorkspaceTypeEnum.BusinessAgency:
    case WorkspaceTypeEnum.BusinessNonAgency:
      return true;

    default:
      return false;
  }
};

export const workspaceTypeIsIndividual = (workspace?: Workspace) => {
  switch (workspace?.workspaceType) {
    case WorkspaceTypeEnum.Individual:
      return true;

    default:
      return false;
  }
};

export const workspaceTypeFromEntityType = (
  entityType: EntityTypeEnum,
  representsClients: string,
) => {
  // Determine workspaceType based on entityType and representsClients
  return entityType === EntityTypeEnum.Individual
    ? WorkspaceTypeEnum.Individual
    : representsClients === 'yes'
      ? WorkspaceTypeEnum.BusinessAgency
      : WorkspaceTypeEnum.BusinessNonAgency;
};

export const isLoadingState = (value: string): boolean => {
  return value === ApiLoadingStateEnum.loading;
};

export const isFailedState = (value: string): boolean => {
  return value === ApiLoadingStateEnum.failed;
};

export const isSuccessState = (value: string): boolean => {
  return value === ApiLoadingStateEnum.succeeded;
};

export const isFinishedState = (value: ApiLoadingStateEnum): boolean => {
  return (
    value === ApiLoadingStateEnum.succeeded ||
    value === ApiLoadingStateEnum.failed
  );
};

// Fastest algorithm
export const removeSpace = (value: string): string => value.replace(/\s+/g, '');

export const isValidPattern = (pattern: string, value: string): boolean => {
  const regex = new RegExp(pattern, 'i');

  return regex.test(value);
};

export const numberFormat = (number: string): string => {
  return number.replace(/\D/g, '');
};

// Camel Case conversion
// Source: javascript.plainenglish.io
// From: John Au-Yeung
// URL: https://javascript.plainenglish.io/how-to-convert-any-string-into-camel-case-with-javascript-1ec0a53ae798
export const toCamelCase = (str: string): string => {
  return (
    str
      .toLowerCase()
      // Replace non alpha-numeric with space
      .replace(/[^a-z0-9 ]/g, ' ')
      // Uppercase letters preceded by a space or number
      .replace(/^\w|[0-9]|\b\w/g, (word: string, index: number) => {
        return index === 0 ? word.toLowerCase() : word.toUpperCase();
      })
      // Remove spaces
      .replace(/\s+/g, '')
  );
};

export const capitalizeWords = (value: string): string => {
  const stringArray = value.split(' ');

  for (let i = 0; i < stringArray.length; i++) {
    stringArray[i] =
      stringArray[i].charAt(0).toUpperCase() + stringArray[i].slice(1);
  }

  return stringArray.join(' ');
};

export const shouldVerifyBusinessNumber = (businessNumberCountry?: string) =>
  businessNumberCountry === 'AU';

export const userIsAllowedCustomerView = (user?: DeepPartial<User>) => {
  const allowedRoles: RoleEnum[] = [
    RoleEnum.CustomerSupportSupervisor,
    RoleEnum.CustomerSupportUser,
    RoleEnum.SystemAdmin,
  ];

  return allowedRoles.some((roleKey) =>
    user?.roles?.map((userRole) => userRole?.roleKey).includes(roleKey),
  );
};

/**
 * Whether a user is Screenrights staff. The single definition of that check
 * -- `AuthHelper` exposes it too, and both layouts, the sidebars and the
 * analytics identity used to spell it out inline.
 *
 * Note the polarity: external is `!internal`, NOT `userType === External`.
 * A user whose `userType` has not loaded counts as external, which is the
 * safe default -- staff-only UI stays hidden and staff traffic is never
 * silently counted as a member. The two are not interchangeable, so a
 * genuine `=== UserTypeEnum.External` check (see WorkspaceSidebar) is left
 * spelled out rather than swapped for `isExternalUser`.
 */
export const isInternalUser = (user?: DeepPartial<User>): boolean =>
  user?.userType === UserTypeEnum.Internal;

export const isExternalUser = (user?: DeepPartial<User>): boolean =>
  !isInternalUser(user);

/** Staff barred from Customer View. External users are false, not true. */
export const userIsDeniedCustomerView = (user?: DeepPartial<User>) =>
  isInternalUser(user) && !userIsAllowedCustomerView(user);

/** The other half of the pair: `userIsAllowedCustomerView` alone admits externals. */
export const userIsInCustomerView = (user?: DeepPartial<User>) =>
  isInternalUser(user) && userIsAllowedCustomerView(user);

export const userIsWorkspaceOwner = (
  user: User | undefined,
  workspaceId?: string,
): boolean => {
  if (!user || !user.roles) return false;

  return user.roles.some(
    (value) =>
      value.roleKey === RoleEnum.WorkspaceOwner &&
      value.workspaceId === workspaceId,
  );
};

export const userIsInvitedAsWorkspaceOwner = (
  invitation: Invitation | undefined,
  workspaceId?: string,
): boolean => {
  if (!invitation || !workspaceId) return false;

  return invitation.userRoles.some(
    (role) =>
      role === RoleEnum.WorkspaceOwner &&
      invitation.workspaceId === workspaceId,
  );
};

export const userIsWorkspaceAdmin = (
  user: User | undefined,
  workspaceId?: string,
): boolean => {
  if (!user || !user.roles || !workspaceId) return false;

  return user.roles.some(
    (value) =>
      value.roleKey === RoleEnum.WorkspaceAdmin &&
      value.workspaceId === workspaceId,
  );
};

export const userMultipleWorkspaceOwner = (
  users: User[],
  workspaceId?: string,
): boolean => {
  if (users?.length === 0) return false;

  return (
    users?.filter((user) => userIsWorkspaceOwner(user, workspaceId)).length > 1
  );
};

export const userIsSystemProcess = (user: User) =>
  user.roles?.some((value) => value.roleKey === RoleEnum.SystemProcess);

export const userIsMe = (user: User, currentUser?: User) =>
  user._id === currentUser?._id;

export const userInitials = (userName: string) => {
  return userName
    ?.replace(/[^a-z0-9 ]/gi, '')
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase())
    .slice(0, 3)
    .join('');
};

export const staffUserHighestRole = (
  roles?: UserRole[],
): UserRole | undefined => {
  if (!roles) return undefined;

  // Role priorities (highest to lowest)
  const rolePriority = [
    RoleEnum.SystemAdmin,
    RoleEnum.PaymentAuthoriser,
    RoleEnum.PaymentPreparer,
    RoleEnum.CustomerSupportSupervisor,
    RoleEnum.CustomerSupportUser,
    RoleEnum.SystemProcess,
  ];

  // Return the first matching role based on priority
  for (const roleKey of rolePriority) {
    const matchingRole = roles.find((role) => role.roleKey === roleKey);

    if (matchingRole) return matchingRole;
  }

  return undefined;
};

/**
 * Returns the highest role of a user found in a workspace,
 * or `undefined`.
 */
export const userHighestRole = (
  user: User | undefined,
  workspaceId?: string,
): UserRole | undefined => {
  if (!user || !user.roles || !workspaceId) return undefined;

  const roles = user.roles.filter(
    (v) => v.workspaceId === workspaceId || v.workspaceId === ALL_WORKSPACES,
  );

  const owner = roles.find(
    (value) => value.roleKey === RoleEnum.WorkspaceOwner,
  );

  if (owner) return owner;

  const admin = roles.find(
    (value) => value.roleKey === RoleEnum.WorkspaceAdmin,
  );

  if (admin) return admin;

  const standard = roles.find(
    (value) => value.roleKey === RoleEnum.WorkspaceUser,
  );

  if (standard) return standard;
};

export const pluralize = (
  count: number,
  singularText: string,
  pluralText: string,
) => `${count} ${count === 1 ? singularText : pluralText}`;

export function uniqueId() {
  const base64chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

  let result = '';
  // Tslint:disable-next-line:insecure-random
  let random = Math.random() * 1073741824; // 5 symbols in base64, almost maxint

  while (random > 0) {
    const char = base64chars.charAt(random % 64);

    result += char;
    random = Math.floor(random / 64);
  }

  return result;
}

export type ThemeColor =
  | 'accent'
  | 'error'
  | 'ghost'
  | 'info'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | undefined;

export const toTitleCase = (text: string): string => {
  if (!text) return '';

  // Replace 'non' with 'Non-' and 'co' with 'Co-' followed by uppercase letters
  const formattedText = text
    .replace(/non/gi, 'Non-')
    .replace(/co([A-Z]+)/g, 'Co-$1');

  // Insert spaces before uppercase letters and numbers
  const spacedText = formattedText
    .replace(/([a-z])([A-Z0-9])/g, '$1 $2')
    .replace(/([0-9])([a-zA-Z])/g, '$1 $2')
    .replace(/-([a-z])/g, (match, p1) => `-${p1.toUpperCase()}`);

  // Capitalize the first character of each word and remove extra spaces
  const titleCasedText = spacedText
    .split(' ')
    .filter((word) => word.trim() !== '') // Remove empty strings from split
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return titleCasedText.trim();
};

export const isNumber = (number: string) => /\d/.test(number);

type BadgeValueProps = {
  color:
    'bg-badge-success' | 'bg-badge-warning' | 'bg-secondary' | 'bg-sr-default';
  label: string;
};

/**
 * @deprecated Uses soon-to-be-removed Tailwind tokens. For service-type pills
 * use `ServiceBadge`, which maps to reusable `Badge` variants instead.
 */
export const SRBadgeProps: Record<'*' | ServiceTypeEnum, BadgeValueProps> = {
  '*': {
    color: 'bg-sr-default',
    label: 'ALL SERVICES',
  },
  collectionAccount: {
    color: 'bg-badge-success',
    label: 'CAM',
  },
  disbursements: {
    color: 'bg-badge-warning',
    label: 'DAS',
  },
  residuals: {
    color: 'bg-sr-default',
    label: 'Residuals',
  },
  /* No Icons and Colors Provided for the ff. */
  royaltiesAnz: {
    color: 'bg-secondary',
    label: 'Royalties ANZ',
  },
  royaltiesWorld: {
    color: 'bg-secondary',
    label: 'Royalties World',
  },
};

export const toMaxDateConverter = (date: string): string => {
  // Parse the original date string
  const originalDate = new Date(date);

  // Set the time to end of the day
  originalDate.setHours(23, 59, 59, 999);

  // Convert to UTC and format the end of day as an ISO string
  return originalDate.toISOString();
};

export const toMinDateConverter = (dateString: string): string => {
  const date = new Date(dateString);

  date.setHours(0, 0, 0, 0);

  return date.toISOString();
};

export const isDefinedAndNotEmpty = (value?: string) => {
  return value !== undefined && value !== null && value !== '';
};

/**
 * True for the Royalties ANZ/World service types. Per APP-2072, a
 * title's `serviceType` is one of two mutually exclusive families —
 * [Royalties ANZ/World] or [CollectionAccount/Disbursements/
 * Residuals] — so this (checked against a title's service types via
 * `.some()`) is the title-level counterpart to the workspace-addon-based
 * `hasRoyaltiesEnabled` below.
 */
export const isRoyaltyServiceType = (serviceType: ServiceTypeEnum): boolean =>
  serviceType === ServiceTypeEnum.RoyaltiesAnz ||
  serviceType === ServiceTypeEnum.RoyaltiesWorld;

/**
 * True when any workspace addon key that includes "royalties" is enabled
 * (today: `anzSecondaryRoyalties`, `royaltiesWorld`). Matching on the key
 * name keeps this resilient if ops add another royalties-* addon without a
 * front-end deploy.
 */
export const hasRoyaltiesEnabled = (workspace?: Workspace | null): boolean => {
  if (!workspace?.addons) {
    return false;
  }

  return (Object.entries(workspace.addons) as [keyof Addons, boolean][]).some(
    ([key, value]) => key.toLowerCase().includes('royalties') && value === true,
  );
};

/**
 * Whether a workspace may access Historic Royalties (MSR2). Requires Current
 * Member status and any royalties addon — a member who holds only
 * `royaltiesWorld` still has MSR2 history worth reading, so the loose check is
 * deliberate here.
 *
 * Region pages are stricter: Royalties ANZ uses {@link canAccessRoyaltiesAnz}
 * and World uses {@link canAccessRoyaltiesWorld}, so neither renders a
 * permanently empty table for a service the workspace does not hold.
 *
 * Product intent (finalised 2026-08): non-current members and workspaces with
 * no royalties addon see no nav link and get not-found on direct URLs.
 *
 * Pass the resolved `isCurrentMember` from
 * {@link useEffectiveMembershipStatus} so a fresh membership record wins over
 * a stale workspace cache.
 */
export const canAccessRoyalties = (
  isCurrentMember: boolean,
  workspace?: Workspace | null,
): boolean => isCurrentMember && hasRoyaltiesEnabled(workspace);

/**
 * True when the workspace has the ANZ royalties addon
 * (`addons.anzSecondaryRoyalties`). Unlike {@link hasRoyaltiesEnabled}, this
 * does not treat World-only workspaces as eligible.
 */
export const hasRoyaltiesAnzEnabled = (workspace?: Workspace | null): boolean =>
  workspace?.addons?.anzSecondaryRoyalties === true;

/**
 * Whether a workspace may access Royalties ANZ (MSR3).
 * Requires Current Member status and `addons.anzSecondaryRoyalties`.
 * Historic Royalties keeps {@link canAccessRoyalties} (any royalties addon).
 */
export const canAccessRoyaltiesAnz = (
  isCurrentMember: boolean,
  workspace?: Workspace | null,
): boolean => isCurrentMember && hasRoyaltiesAnzEnabled(workspace);

/**
 * True when the workspace has the Royalties World addon
 * (`addons.royaltiesWorld`). Unlike {@link hasRoyaltiesEnabled}, this does
 * not treat ANZ-only workspaces as eligible.
 */
export const hasRoyaltiesWorldEnabled = (
  workspace?: Workspace | null,
): boolean => workspace?.addons?.royaltiesWorld === true;

/**
 * Whether a workspace may access Royalties World (MSR3).
 * Requires Current Member status and `addons.royaltiesWorld`.
 * Historic Royalties / Royalties ANZ keep {@link canAccessRoyalties}.
 */
export const canAccessRoyaltiesWorld = (
  isCurrentMember: boolean,
  workspace?: Workspace | null,
): boolean => isCurrentMember && hasRoyaltiesWorldEnabled(workspace);

/**
 * True when the workspace has Disbursements (collection accounts) enabled
 * (`addons.collectionAccounts` or `addons.collectionAccountSource`).
 */
export const hasDisbursementsEnabled = (
  workspace?: Workspace | null,
): boolean =>
  workspace?.addons?.collectionAccounts === true ||
  workspace?.addons?.collectionAccountSource === true;

/**
 * True when the workspace has Residuals enabled
 * (`addons.residualsManagement` or `addons.residualsBeneficiary`).
 */
export const hasResidualsEnabled = (workspace?: Workspace | null): boolean =>
  workspace?.addons?.residualsManagement === true ||
  workspace?.addons?.residualsBeneficiary === true;

/**
 * Checks if a workspace is "royalties only" (no CAM/DAS or residuals features).
 * A workspace is considered royalties only if:
 * - collectionAccounts (CAM/DAS) is not enabled
 * - residuals is not enabled
 *
 * @param workspace - The workspace to check
 * @returns true if the workspace is royalties only, false otherwise
 */
export const isRoyaltiesOnlyWorkspace = (
  workspace?: Workspace | null,
): boolean => {
  if (!workspace?.addons) {
    return false;
  }

  //
  return (
    (Object.entries(workspace.addons) as [keyof Addons, boolean][]).every(
      ([key, value]) => {
        // is "royalties only" when every entry is either false or the key contains "royalties" if true
        return value === false || key.toLowerCase().includes('royalties');
      },
    ) && hasRoyaltiesEnabled(workspace)
  );
};

export const getTimestampDaysAgo = (days: number) => {
  const now = new Date();

  now.setDate(now.getDate() - days);

  return now.getTime();
};

export const getCurrentUserFullName = (user?: User) =>
  `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();

export const validateWorkspaceName = (workspaceName: string) => {
  const workspaceNameRegex = /^[\p{L}\p{N}\p{Z}\p{M}()Ññ&-]*$/u;

  return workspaceNameRegex.test(workspaceName);
};

export const useOnSuccess = (
  value: string,
  callback?: () => void,
  cleanup?: () => void,
) => {
  useEffect(() => {
    if (isSuccessState(value)) {
      callback?.();
      cleanup?.();
    }

    return cleanup;
  }, [value]);
};

/**
 * Earliest stages of the membership application pipeline (before review / approval).
 * Entity-name edit eligibility in settings (non-AU) is tied to this set.
 */
const MEMBERSHIP_APPLICATION_EARLY_STATUSES = [
  MembershipStatusEnum.ApplicationSent,
  MembershipStatusEnum.AwaitingUpdate,
] as const satisfies readonly MembershipStatusEnum[];

/**
 * Superset: application is already in flight — user must not start another application.
 * Built from {@link MEMBERSHIP_APPLICATION_EARLY_STATUSES} so shared stages stay aligned.
 */
const MEMBERSHIP_APPLICATION_IN_PROGRESS_STATUSES: readonly MembershipStatusEnum[] =
  [
    ...MEMBERSHIP_APPLICATION_EARLY_STATUSES,
    MembershipStatusEnum.InReview,
    MembershipStatusEnum.PendingApproval,
  ];

/**
 * Application under assessment: Entity Details are frozen for external users
 * (APP-2254). Both assessment stages count — `InReview` while staff assess the
 * application and `PendingApproval` while it waits on the approval decision —
 * while the surrounding stages stay editable so corrections remain possible.
 *
 * The API keeps its own copy in `LOCKED_MEMBERSHIP_STATUSES`
 * (entity-details-lock.ts); no package is imported by both sides, so keep the
 * two in step or the UI offers fields the API refuses.
 */
export const MEMBERSHIP_UNDER_REVIEW_STATUSES: readonly MembershipStatusEnum[] =
  [MembershipStatusEnum.InReview, MembershipStatusEnum.PendingApproval];

/**
 * Before any application exists, and during the membership application phase
 * (applicationSent / awaitingUpdate), external workspace-owner or
 * workspace-admin users may edit the entity name: Apply needs it filled in
 * (APP-2386), and corrections can be made before final review.
 * AU workspaces are excluded because entity name is sourced from ABN lookup.
 */
export const isEntityNameEditableForApplication = (
  workspace?: Workspace,
  user?: User,
): boolean => {
  if (!workspace || !user) return false;

  // AU workspaces cannot edit their entityName in settings or onboarding
  if (workspace.businessNumberCountry === 'AU') return false;

  // Only external users can edit the entityName
  const userIsExternal = isExternalUser(user);

  if (!userIsExternal) return false;

  // Only workspace owner or admin can edit the entityName
  const isOwnerOrAdmin =
    userIsWorkspaceOwner(user, workspace._id) ||
    userIsWorkspaceAdmin(user, workspace._id);

  if (!isOwnerOrAdmin) return false;

  return (
    isAllowedToApplyMembership(workspace, user) ||
    MEMBERSHIP_APPLICATION_EARLY_STATUSES.some(
      (s) => s === workspace.membershipStatus,
    )
  );
};

export const isAllowedToApplyMembership = (
  workspace?: Workspace,
  user?: User,
) => {
  if (!workspace || !user) return false;

  // Don't allow if already a current member
  if (workspace.membershipStatus === MembershipStatusEnum.CurrentMember) {
    return false;
  }

  // Don't allow if application is already in progress
  const applicationInProgress =
    MEMBERSHIP_APPLICATION_IN_PROGRESS_STATUSES.some(
      (s) => s === workspace.membershipStatus,
    );

  if (applicationInProgress) {
    return false;
  }

  // If the workspace has a membershipId, it means it has already been applied
  const isMembershipIdNull = workspace.membershipId == null;

  return isMembershipIdNull;
};

export const useIsFeatureEnabled = (name: string) => {
  const featuresEnabled = useTypedSelector(
    reduxServices.setting.selectors.features,
  );

  return featuresEnabled?.[name] === 'true';
};

/**
 * Filters out empty values from a data object
 * @param data - The data object to filter
 * @returns Filtered data object with only non-empty values, or undefined if all values are empty
 */
export const cleanObjForForm = <T extends object>(
  data?: T | null,
): T | undefined => {
  if (!data) return undefined;

  const filtered = Object.fromEntries(
    Object.entries(data).filter(
      ([, value]) => value !== '' && value !== null && value !== undefined,
    ),
  ) as T;

  return Object.keys(filtered).length > 0 ? filtered : undefined;
};

/**
 * Tags must be simple key-value pairs
 * of type `string` and cannot contain spaces.
 *
 * They must also have a maximum length of 32 characters
 * and can contain only letters (a-zA-Z), numbers (0-9),
 * underscores (_), periods (.), colons (:), and dashes (-).
 *
 * https://docs.sentry.io/platforms/javascript/guides/react/enriching-events/tags/
 */
export const convertStringToSentryTag = (value: string) => {
  return value
    .replace(/[^a-zA-Z0-9_.:-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .substring(0, 32)
    .trim();
};

export const TIME = {
  minutesToMs: (minutes: number) => {
    return minutes * 60 * 1000;
  },
};

// The task list's alphabetical sort repeats this rule in Mongo (`SORT_TITLE_FIELDS`
// in the tasks `task.service.ts`); change both together.
export const getOriginalTitle = (task: WorkspaceTask): string => {
  const originalTitle = task?.details?.titles?.find(
    (title) => title?.originalTitle && Boolean(title?.title?.trim()),
  )?.title;

  return originalTitle?.trim() || task?.description?.trim() || 'Untitled';
};

export const formatTaskSubtitle = (
  task: WorkspaceTask,
  clientName?: string,
): string => {
  const subtitleParts = [
    task?.details?.yearOfProduction,
    task?.details?.workType,
    clientName,
  ].filter(Boolean);

  return subtitleParts.join(' | ');
};

/**
 * Sanitise a redirect target so it can only navigate within the same origin.
 *
 * Rejects (returning `fallback`):
 *  - Empty / null / non-string input
 *  - Absolute URLs pointing to a different origin (unless that origin is
 *    in `allowedOrigins`, in which case the full URL is returned — used
 *    for cross-app flows e.g. /auth/mfa-setup bouncing back to MSR2)
 *  - Protocol-relative (`//host`) and backslash-escaped (`/\host`) URLs,
 *    which some browsers still resolve to `//host`
 *  - URLs containing control characters (tab, newline, NUL, ...), which
 *    certain browsers strip before resolving
 *  - Dangerous protocols (`javascript:`, `data:`, `vbscript:`, `file:`,
 *    `blob:`) even if they happen to parse to the same origin
 *  - Paths that do not start with `/` after normalisation
 *  - Anything that fails to parse
 *
 * @param allowedOrigins exact origin strings (scheme + host + port, no
 *   trailing slash — matches `URL.origin`). An absolute http(s) URL
 *   whose origin is listed is returned verbatim. Empty by default keeps
 *   strict same-origin behavior for existing callers.
 */
export const safeRedirect = (
  url: null | string | undefined,
  fallback = '/',
  allowedOrigins: string[] = [],
): string => {
  if (typeof url !== 'string' || url.length === 0) return fallback;

  // eslint-disable-next-line no-control-regex -- lint-backlog: matches control characters on purpose
  if (/[\x00-\x1F\x7F]/.test(url)) return fallback;

  const normalised = url.replace(/\\/g, '/').trim();

  if (normalised.length === 0) return fallback;

  if (normalised.startsWith('//')) return fallback;

  const dangerousProtocol =
    /^(javascript|data|vbscript|file|blob|about):/i.test(normalised);

  if (dangerousProtocol) return fallback;

  if (allowedOrigins.length > 0 && /^https?:\/\//i.test(normalised)) {
    try {
      const parsed = new URL(normalised);

      if (allowedOrigins.includes(parsed.origin)) {
        return parsed.toString();
      }
    } catch {
      // fall through to fallback
    }

    return fallback;
  }

  if (!normalised.startsWith('/')) return fallback;

  try {
    const parsed = new URL(normalised, window.location.origin);

    if (parsed.origin !== window.location.origin) return fallback;

    const path = parsed.pathname + parsed.search + parsed.hash;

    if (!path.startsWith('/') || path.startsWith('//')) return fallback;

    return path;
  } catch {
    return fallback;
  }
};

export const normalizeMessageValue = (value?: string): string | undefined =>
  value?.trim().toLowerCase();

export const formatSeasonNo = (value?: null | string) => value?.trim() || '—';
